import api from '../api';

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const MONTHLY_DIALOG_SELECTOR = '[data-inventory-monthly-dialog="true"]';

let installed = false;
let monthlySelected = false;
let exporting = false;
let dialogOpen = false;

const optionValues = (select) =>
  new Set(Array.from(select?.options || []).map((option) => option.value));

const isReportFormatSelect = (element) => {
  if (!(element instanceof HTMLSelectElement)) return false;
  const values = optionValues(element);
  return values.has('pdf') && values.has('xlsx') && values.has('xls');
};

const ensureMonthlyOption = (select) => {
  if (!isReportFormatSelect(select)) return;
  if (!Array.from(select.options).some((option) => option.value === 'monthly')) {
    const option = document.createElement('option');
    option.value = 'monthly';
    option.textContent = 'Mensal';
    select.appendChild(option);
  }
  if (monthlySelected) select.value = 'monthly';
};

const findReportFormatSelect = () =>
  Array.from(document.querySelectorAll('select')).find(isReportFormatSelect) || null;

const installOptionObserver = () => {
  const synchronize = () => {
    document.querySelectorAll('select').forEach(ensureMonthlyOption);
  };
  synchronize();
  const observer = new MutationObserver(synchronize);
  observer.observe(document.body, { childList: true, subtree: true });
};

const findSelect = (scope, matcher) =>
  Array.from(scope.querySelectorAll('select')).find(matcher) || null;

const hasAllOption = (select, label) =>
  Array.from(select.options).some(
    (option) => option.value === 'all' && option.textContent?.trim() === label,
  );

const collectActiveFilters = (reportSelect) => {
  const scope = reportSelect?.parentElement || document;
  const warehouse = findSelect(scope, (select) =>
    hasAllOption(select, 'Todos os depósitos'),
  );
  const category = findSelect(scope, (select) =>
    hasAllOption(select, 'Todas as categorias'),
  );
  const status = findSelect(scope, (select) => {
    const values = optionValues(select);
    return (
      values.has('ok') &&
      values.has('low') &&
      values.has('excess') &&
      values.has('inactive')
    );
  });
  const search = Array.from(scope.querySelectorAll('input[type="search"]')).find(
    (input) =>
      String(input.getAttribute('placeholder') || '')
        .toLowerCase()
        .includes('buscar por nome'),
  );

  return {
    warehouseId:
      warehouse?.value && warehouse.value !== 'all'
        ? warehouse.value
        : undefined,
    categoryId:
      category?.value && category.value !== 'all'
        ? category.value
        : undefined,
    status:
      status?.value && status.value !== 'all' ? status.value : undefined,
    search: search?.value?.trim() || undefined,
  };
};

const style = (element, rules) => {
  Object.assign(element.style, rules);
  return element;
};

const button = (label, primary = false) => {
  const element = document.createElement('button');
  element.type = 'button';
  element.textContent = label;
  return style(element, {
    border: primary ? '1px solid #4f46e5' : '1px solid #cbd5e1',
    borderRadius: '999px',
    padding: '10px 18px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    background: primary ? '#4f46e5' : '#ffffff',
    color: primary ? '#ffffff' : '#475569',
  });
};

const fieldLabel = (text, control) => {
  const label = document.createElement('label');
  label.textContent = text;
  style(label, {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    color: '#475569',
    fontSize: '13px',
    fontWeight: '600',
  });
  style(control, {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px solid #cbd5e1',
    borderRadius: '12px',
    padding: '10px 12px',
    fontSize: '14px',
    background: '#ffffff',
    color: '#334155',
  });
  label.appendChild(control);
  return label;
};

const openPeriodDialog = () => {
  if (dialogOpen || document.querySelector(MONTHLY_DIALOG_SELECTOR)) {
    return Promise.resolve(null);
  }

  dialogOpen = true;
  return new Promise((resolve) => {
    const today = new Date();
    const overlay = style(document.createElement('div'), {
      position: 'fixed',
      inset: '0',
      zIndex: '10000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      background: 'rgba(15, 23, 42, 0.55)',
    });
    overlay.dataset.inventoryMonthlyDialog = 'true';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute(
      'aria-label',
      'Selecionar período do controle mensal de estoque',
    );

    const panel = style(document.createElement('div'), {
      width: '100%',
      maxWidth: '440px',
      borderRadius: '18px',
      background: '#ffffff',
      boxShadow: '0 24px 70px rgba(15, 23, 42, 0.28)',
      padding: '24px',
      color: '#0f172a',
      fontFamily: 'inherit',
    });
    const title = document.createElement('h3');
    title.textContent = 'Exportar controle mensal';
    style(title, { margin: '0', fontSize: '20px', fontWeight: '700' });
    const description = document.createElement('p');
    description.textContent =
      'Selecione o mês e o ano. Os filtros aplicados nos itens em estoque serão mantidos no relatório.';
    style(description, {
      margin: '8px 0 20px',
      color: '#64748b',
      fontSize: '14px',
      lineHeight: '1.5',
    });

    const month = document.createElement('select');
    MONTHS.forEach((name, index) => {
      const option = document.createElement('option');
      option.value = String(index + 1);
      option.textContent = name;
      month.appendChild(option);
    });
    month.value = String(today.getMonth() + 1);

    const year = document.createElement('input');
    year.type = 'number';
    year.min = '2000';
    year.max = '2100';
    year.step = '1';
    year.value = String(today.getFullYear());

    const fields = style(document.createElement('div'), {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '14px',
    });
    fields.append(fieldLabel('Mês', month), fieldLabel('Ano', year));

    const cancel = button('Cancelar');
    const confirm = button('Gerar planilha', true);
    confirm.dataset.inventoryMonthlyConfirm = 'true';
    const actions = style(document.createElement('div'), {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '10px',
      marginTop: '24px',
    });
    actions.append(cancel, confirm);
    panel.append(title, description, fields, actions);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    let closed = false;
    const close = (value) => {
      if (closed) return;
      closed = true;
      dialogOpen = false;
      document.removeEventListener('keydown', onKeyDown, true);
      overlay.remove();
      resolve(value);
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') close(null);
    };
    document.addEventListener('keydown', onKeyDown, true);
    overlay.addEventListener('click', (event) => {
      if (event.target === overlay) close(null);
    });
    cancel.addEventListener('click', () => close(null));
    confirm.addEventListener('click', () => {
      const selectedMonth = Number(month.value);
      const selectedYear = Number(year.value);
      if (!Number.isInteger(selectedMonth) || selectedMonth < 1 || selectedMonth > 12) {
        window.alert('Selecione um mês válido.');
        return;
      }
      if (!Number.isInteger(selectedYear) || selectedYear < 2000 || selectedYear > 2100) {
        window.alert('Informe um ano válido entre 2000 e 2100.');
        return;
      }
      close({ month: selectedMonth, year: selectedYear });
    });
    setTimeout(() => month.focus(), 0);
  });
};

const downloadFile = (response, fallbackName) => {
  const disposition = response.headers?.['content-disposition'] || '';
  const match = disposition.match(
    /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i,
  );
  const serverName = match?.[1] || match?.[2];
  const fileName = serverName
    ? decodeURIComponent(serverName.replace(/"/g, ''))
    : fallbackName;
  const contentType =
    response.headers?.['content-type'] ||
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob =
    response.data instanceof Blob
      ? response.data
      : new Blob([response.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const errorMessage = async (error) => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const payload = JSON.parse(await data.text());
      if (payload?.error) return payload.error;
    } catch {
      // Keep the generic message when the error body is not JSON.
    }
  }
  return (
    error?.response?.data?.error ||
    error?.message ||
    'Não foi possível gerar o controle mensal de estoque.'
  );
};

const exportMonthly = async (exportButton, reportSelect) => {
  if (exporting || dialogOpen) return;
  const period = await openPeriodDialog();
  if (!period) return;

  exporting = true;
  const originalHtml = exportButton.innerHTML;
  exportButton.disabled = true;
  exportButton.setAttribute('aria-busy', 'true');
  exportButton.textContent = 'Gerando...';
  try {
    const response = await api.get('/inventory/report', {
      params: {
        ...collectActiveFilters(reportSelect),
        format: 'monthly',
        month: period.month,
        year: period.year,
      },
      responseType: 'blob',
    });
    downloadFile(
      response,
      `controle-mensal-estoque-${period.year}-${String(period.month).padStart(2, '0')}.xlsx`,
    );
  } catch (error) {
    window.alert(await errorMessage(error));
  } finally {
    exporting = false;
    exportButton.disabled = false;
    exportButton.removeAttribute('aria-busy');
    exportButton.innerHTML = originalHtml;
  }
};

const installEvents = () => {
  document.addEventListener(
    'change',
    (event) => {
      const select = event.target;
      if (!isReportFormatSelect(select)) return;
      if (select.value === 'monthly') {
        monthlySelected = true;
        event.stopPropagation();
        setTimeout(() => ensureMonthlyOption(select), 0);
      } else {
        monthlySelected = false;
      }
    },
    true,
  );

  document.addEventListener(
    'click',
    (event) => {
      const exportButton = event.target?.closest?.('button');
      if (!exportButton || !/^\s*exportar\s*$/i.test(exportButton.textContent || '')) {
        return;
      }
      if (exportButton.closest(MONTHLY_DIALOG_SELECTOR)) {
        return;
      }
      const reportSelect = findReportFormatSelect();
      if (!reportSelect || (!monthlySelected && reportSelect.value !== 'monthly')) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (dialogOpen || exporting) return;
      exportMonthly(exportButton, reportSelect);
    },
    true,
  );
};

export const installInventoryMonthlyExport = () => {
  if (
    installed ||
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }
  installed = true;
  const start = () => {
    installOptionObserver();
    installEvents();
  };
  if (document.body) start();
  else window.addEventListener('DOMContentLoaded', start, { once: true });
};
